import { ActivityIndicator, Pressable, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import CutCard from '@/components/General/CutCard';
import useOrganizationSetup from '@/utils/hooks/useOrganizationSetup';
import { canManageOrganizationSetup, ORGANIZATION_SETUP_TASKS, setupProgress } from '@/config/organizationSetup';

export default function OrganizationSetupCard({ user, expanded = false, fullWidth = false }) {
    const query = useOrganizationSetup(user);
    const router = useRouter();
    const { items, resolved, total, complete } = setupProgress(query.data);
    if (!canManageOrganizationSetup(user)) return null;
    if (!expanded && (!total || complete)) return null;
    const pending = items.filter(item => item.status === 'pending');
    const shown = expanded ? items : pending.slice(0, 3);
    const nextID = pending[0]?.id;
    return <CutCard testID="organization-setup-card" accentColor="primary"
        style={[styles.card, (expanded || fullWidth) && styles.expanded]} contentStyle={styles.cardContent}>
        <View style={styles.header}>
            <View style={styles.heading}>
                <Unicon name="cogs" library="fa5" size={19} colorVariant="primary" />
                <Text style={styles.title}>Set up your organization</Text>
            </View>
            {!!total && <Text testID="organization-setup-progress" style={styles.count}>{resolved} of {total}</Text>}
        </View>
        <Text style={styles.subtitle}>{complete ? 'Your setup is resolved. You can revisit these areas anytime.' : 'One step at a time. Progress is shared with your admin team.'}</Text>
        {!!total && <View style={styles.track}><View style={[styles.fill, { width: `${resolved / total * 100}%` }]} /></View>}
        {query.isPending && <ActivityIndicator />}
        {query.isError && <View style={styles.error}>
            <Text style={styles.description}>{query.unavailable ? 'The setup checklist is not available yet.' : 'Setup progress could not be loaded.'}</Text>
            <SolidButton text="Try again" size="sm" variant="muted" onPress={() => query.refetch()} />
        </View>}
        {shown.map(item => {
            const task = ORGANIZATION_SETUP_TASKS[item.id];
            const done = item.status !== 'pending';
            return <View key={item.id} testID={`setup-task-${item.id}`} style={styles.task}>
                <View style={styles.taskRow}>
                    <Pressable
                        testID={`setup-complete-${item.id}`}
                        accessibilityRole="checkbox"
                        accessibilityLabel={`Mark ${task.title} complete for your organization`}
                        accessibilityState={{ checked: done, disabled: done || query.resolve.isPending }}
                        disabled={done || query.resolve.isPending}
                        style={styles.checkbox}
                        onPress={() => query.resolve.mutate({ taskID: item.id, status: 'completed' })}
                    >
                        <Unicon name={item.status === 'dismissed' ? 'remove-circle-outline' : done ? 'checkmark-circle' : 'ellipse-outline'}
                            size={25} colorVariant={done ? 'success' : 'textTertiary'} />
                    </Pressable>
                    <View style={styles.taskCopy}>
                        <Text style={styles.taskTitle}>{task.title}</Text>
                        {done && <Text style={styles.description}>{item.status === 'dismissed' ? 'Dismissed by your organization' : 'Completed'}</Text>}
                    </View>
                    {!done && <Pressable testID={`setup-dismiss-${item.id}`} accessibilityRole="button"
                        accessibilityLabel={`Dismiss ${task.title} for your organization`}
                        disabled={query.resolve.isPending} style={styles.dismiss}
                        onPress={() => query.resolve.mutate({ taskID: item.id, status: 'dismissed' })}>
                        <Text style={styles.dismissText}>Dismiss</Text>
                    </Pressable>}
                </View>
                {(expanded || !done) && <View style={styles.taskBody}>
                    {(expanded || item.id === nextID) && <>
                        <Text style={styles.description}>{task.description}</Text>
                        {item.id === 'giving' && query.data?.givingPending && !done
                            && <Text testID="setup-giving-pending" style={styles.pending}>Application submitted. Awaiting approval.</Text>}
                    </>}
                    <SolidButton testID={`setup-open-${item.id}`} text={done ? 'Open settings' : task.action}
                        size="sm" variant="muted" iconName={task.icon} iconBefore onPress={() => router.push(task.route)} />
                </View>}
            </View>;
        })}
        {!expanded && <Pressable testID="setup-view-all" accessibilityRole="button" style={styles.viewAll}
            onPress={() => router.push('/settings/organization/setup')}>
            <Text style={styles.link}>View all setup tasks</Text>
            <Unicon name="arrow-forward" size={17} colorVariant="primary" />
        </Pressable>}
        {expanded && <Text style={styles.description}>Already handled something? Check it off. Do not need it? Dismiss it. Both changes apply to your organization.</Text>}
    </CutCard>;
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    card: { width: { xs: '100%', lg: '48.5%' }, _web: { width: '100%' } },
    cardContent: { padding: spacing.md, gap: spacing.sm },
    expanded: { width: '100%' },
    header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
    heading: { flexDirection: 'row', alignItems: 'center', flex: 1, gap: spacing.sm },
    title: { flex: 1, fontFamily: fonts.bold, fontSize: typography.body, color: colors.text },
    count: { fontFamily: fonts.bold, fontSize: typography.caption, color: colors.primary, fontVariant: ['tabular-nums'] },
    subtitle: { fontFamily: fonts.body, fontSize: typography.caption, lineHeight: 20, color: colors.textSecondary },
    track: { height: 5, borderRadius: 3, backgroundColor: colors.surfaceVariant, overflow: 'hidden', marginVertical: spacing.xs },
    fill: { height: '100%', backgroundColor: colors.success, borderRadius: 3 },
    task: { gap: spacing.xs },
    taskRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
    checkbox: { minWidth: 40, minHeight: 44, alignItems: 'center', justifyContent: 'center' },
    taskCopy: { flex: 1, gap: 2 },
    taskTitle: { fontFamily: fonts.bold, fontSize: typography.body, color: colors.text },
    taskBody: { marginLeft: 44, alignItems: 'flex-start', gap: spacing.sm, paddingBottom: spacing.sm },
    description: { fontFamily: fonts.body, fontSize: typography.caption, lineHeight: 20, color: colors.textSecondary },
    dismiss: { minHeight: 44, paddingHorizontal: spacing.xs, justifyContent: 'center' },
    dismissText: { fontFamily: fonts.body, fontSize: typography.caption, color: colors.textSecondary },
    pending: { fontFamily: fonts.bold, fontSize: typography.caption, color: colors.warning },
    viewAll: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, alignSelf: 'flex-start', paddingVertical: spacing.sm },
    link: { fontFamily: fonts.bold, fontSize: typography.caption, color: colors.primary },
    error: { gap: spacing.sm, alignItems: 'flex-start' },
}));
